/**
 * StatsGrid.tsx
 *
 * File-level:
 * - Enhanced StatsGrid component for the admin dashboard.
 * - Applies carefully chosen gradient backgrounds and high-contrast text to each stat card
 *   to improve visual hierarchy and clarity.
 *
 * Notes:
 * - Uses CSS variables defined in src/styles/theme.css where possible for consistent theming.
 * - Each card renders as a small gradient panel with an icon and bold numeric value.
 */

import React from 'react';
import { IndianRupeeIcon, UserPlus, Users, AlertCircle } from 'lucide-react';

/**
 * Stats
 *
 * @description Shape of the stats object consumed by StatsGrid.
 */
export interface Stats {
  /** Total number of profiles in system */
  totalProfiles: number;
  /** New profiles added recently */
  newProfiles: number;
  /** Count of paid profiles */
  paidProfiles: number;
  /** Count of not-paid profiles (computed or provided) */
  notPaidProfiles?: number;
  /** Number of forgot-password requests */
  forgotPasswordCount: number;
}

/**
 * StatCardProps
 *
 * @description Props for the small StatCard component.
 */
interface StatCardProps {
  title: string;
  value: number | string;
  /**
   * gradient - CSS gradient to use as card background.
   * Accepts a CSS gradient expression or a CSS variable name (e.g. 'var(--card-gradient-new)').
   */
  gradient?: string;
  /**
   * iconColor - color applied to the icon (useful when the icon sits on a white circle).
   */
  icon?: React.ReactNode;
  onClick?: () => void;
  testId?: string;
}

/**
 * StatCard
 *
 * @description Reusable presentational card that displays a stat with a bold numeric value.
 * It uses a full-card gradient background and white text for clear contrast.
 *
 * @param props StatCardProps
 */
function StatCard({ title, value, gradient, icon, onClick, testId }: StatCardProps) {
  const bgStyle = gradient ? { backgroundImage: gradient } : undefined;

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      aria-label={String(title)}
      className="w-full text-left rounded-lg shadow-md transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={bgStyle}
    >
      <div className="flex items-center gap-4 p-4 rounded-lg" style={{ background: 'transparent' }}>
        <div
          className="flex items-center justify-center w-12 h-12 rounded-lg bg-white/20"
          aria-hidden="true"
        >
          <div className="w-6 h-6 text-white">{icon}</div>
        </div>

        <div className="flex-1">
          <div className="text-sm font-medium text-white/90">{title}</div>
          <div className="text-2xl font-extrabold text-white mt-1">{value}</div>
        </div>
      </div>
    </button>
  );
}

/**
 * StatsGridProps
 *
 * @description Props for StatsGrid component.
 */
interface StatsGridProps {
  stats: Stats;
  onCardClick?: (key: keyof Stats | string) => void;
}

/**
 * getGradientForKey
 *
 * @description Returns a CSS gradient (using CSS variables when available) for each stat key.
 * This keeps the dashboard visually consistent with theme.css while allowing a custom gradient
 * for the new 'Not Paid Profiles' card.
 *
 * @param key string - stat key
 * @returns string - CSS background-image value
 */
function getGradientForKey(key: string): string {
  switch (key) {
    case 'totalProfiles':
      // Uses theme variable for total (violet -> pink)
      return 'var(--card-gradient-total)';
    case 'newProfiles':
      // Indigo -> cyan
      return 'var(--card-gradient-new)';
    case 'paidProfiles':
      // Emerald -> teal
      return 'var(--card-gradient-paid)';
    case 'notPaidProfiles':
      // Custom warm-rose gradient (contrasts well next to green paid card)
      return 'linear-gradient(90deg, rgba(244,63,94,1) 0%, rgba(236,72,153,1) 100%)';
    case 'forgotPasswordCount':
      // Amber -> orange (theme variable)
      return 'var(--card-gradient-forgot)';
    default:
      return 'linear-gradient(90deg, rgba(99,102,241,1) 0%, rgba(6,182,212,1) 100%)';
  }
}

/**
 * StatsGrid
 *
 * @description Renders a responsive grid of stat cards for the admin dashboard.
 * The layout is tuned so that on large screens the cards form a single row and the
 * Not Paid Profiles card sits beside Paid Profiles for easy comparison.
 *
 * @param props StatsGridProps
 */
export default function StatsGrid({ stats, onCardClick }: StatsGridProps) {
  const {
    totalProfiles,
    newProfiles,
    paidProfiles,
    notPaidProfiles = 0,
    forgotPasswordCount,
  } = stats;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div className="lg:col-span-1">
        <StatCard
          title="Total Profiles"
          value={totalProfiles}
          gradient={getGradientForKey('totalProfiles')}
          icon={<Users className="w-5 h-5" />}
          onClick={() => onCardClick?.('totalProfiles')}
          testId="stat-total-profiles"
        />
      </div>

      <div className="lg:col-span-1">
        <StatCard
          title="New Profiles"
          value={newProfiles}
          gradient={getGradientForKey('newProfiles')}
          icon={<UserPlus className="w-5 h-5" />}
          onClick={() => onCardClick?.('newProfiles')}
          testId="stat-new-profiles"
        />
      </div>

      <div className="lg:col-span-1">
        <StatCard
          title="Paid Profiles"
          value={paidProfiles}
          gradient={getGradientForKey('paidProfiles')}
          icon={<IndianRupeeIcon className="w-5 h-5" />}
          onClick={() => onCardClick?.('paidProfiles')}
          testId="stat-paid-profiles"
        />
      </div>

      <div className="lg:col-span-1">
        <StatCard
          title="Not Paid Profiles"
          value={notPaidProfiles}
          gradient={getGradientForKey('notPaidProfiles')}
          icon={<IndianRupeeIcon className="w-5 h-5" />}
          onClick={() => onCardClick?.('notPaidProfiles')}
          testId="stat-not-paid-profiles"
        />
      </div>

      <div className="lg:col-span-1">
        <StatCard
          title="Forgot Password"
          value={forgotPasswordCount}
          gradient={getGradientForKey('forgotPasswordCount')}
          icon={<AlertCircle className="w-5 h-5" />}
          onClick={() => onCardClick?.('forgotPasswordCount')}
          testId="stat-forgot-password"
        />
      </div>
    </div>
  );
}